<template>
  <div :class="{ 'p-2': padContent }" ref="navigationListContainer">
    <div class="mb-4">
      <h2 v-if="title" ref="titleElement" class="h5 mb-3 text-dark fw-bold">
        {{ title }}
      </h2>

      <div class="d-flex flex-column gap-2 text-muted small">
        <div class="d-flex gap-2">
          <i
            class="bi bi-cursor-fill mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Select an island or area from the lists below, or 
            click any marker on the map to view details.</span
          >
        </div>

        <div class="d-flex gap-2">
          <i
            class="bi bi-layers-fill mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Use the <strong>Map Tools</strong> panel to toggle layers like
            Cultural Regions and Migration Routes. Each layer reveals different
            areas you can explore.</span
          >
        </div>

        <div class="d-flex gap-2">
          <i
            class="bi bi-bounding-box mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Areas in the list below (marked with
            <i class="bi bi-bounding-box" aria-hidden="true"></i>) represent
            regions and migration zones. Selecting an area will automatically
            show its layer.</span
          >
        </div>

        <div class="d-flex gap-2">
          <i
            class="bi bi-arrows-move mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Use tab keys to navigate the list, Enter to select, and Escape to
            return to the list.</span
          >
        </div>
      </div>
    </div>

    <hr class="my-4 text-muted opacity-25" />

    <!-- Areas Section -->
    <div v-if="areas && areas.length > 0" class="mb-4">
      <h3
        class="h6 text-uppercase text-muted fw-bold mb-3"
        style="letter-spacing: 0.05em"
      >
        Areas & Regions
      </h3>

      <div class="island-list" role="list" aria-label="Areas and regions">
        <button
          v-for="area in areas"
          :key="area.id"
          type="button"
          class="island-item area-item"
          :aria-label="`Select ${area.name}, area or region`"
          role="listitem"
          @click="handleAreaSelect(area)"
          @keydown="handleKeyDown($event, area, 'area')"
        >
          <div class="d-flex align-items-center gap-2">
            <i
              class="bi bi-bounding-box area-icon flex-shrink-0"
              aria-hidden="true"
            ></i>
            <span class="island-name">{{ area.name }}</span>
          </div>
          <i class="bi bi-chevron-right island-chevron" aria-hidden="true"></i>
        </button>
      </div>

      <hr class="my-4 text-muted opacity-25" />
    </div>

    <!-- Islands Section -->
    <h3
      class="h6 text-uppercase text-muted fw-bold mb-3"
      style="letter-spacing: 0.05em"
    >
      Islands
    </h3>

    <div class="island-list" role="list" aria-label="Islands">
      <button
        v-for="island in sortedIslands"
        :key="island[IslandHeaders.ID]"
        type="button"
        class="island-item"
        :aria-label="`Select ${island[IslandHeaders.NAME]}, island`"
        role="listitem"
        @click="handleSelect(island)"
        @keydown="handleKeyDown($event, island, 'island')"
      >
        <span class="island-name">{{ island[IslandHeaders.NAME] }}</span>
        <i class="bi bi-chevron-right island-chevron" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { IslandHeaders } from "@/types/index.ts";

export default {
  name: "IslandNavigationList",
  data() {
    return {
      IslandHeaders
    };
  },
  props: {
    /**
     * The title of the list
     */
    title: {
      type: String,
      default: null
    },
    /**
     * Whether to pad the content of the list
     */
    padContent: {
      type: Boolean,
      default: false
    },
    /**
     * Array of island objects to display in the list
     */
    islands: {
      type: Array,
      required: true
    },
    /**
     * Array of area objects (polygons) to display in the list.
     * Each area should have: id, name, type, layer, and any CSV data.
     * The layer property indicates which map layer to activate when selected.
     */
    areas: {
      type: Array,
      default: () => []
    },
    /**
     * Field name to use for the island ID (for key binding)
     */
    idField: {
      type: String,
      default: IslandHeaders.ID
    },
    /**
     * Field name to use for display name
     */
    nameField: {
      type: String,
      default: IslandHeaders.NAME
    },
    /**
     * Sort order for the island list
     * Options: 'alphabetical', 'original'
     */
    sortBy: {
      type: String,
      default: "original",
      validator: (value) => ["alphabetical", "original"].includes(value)
    }
  },
  emits: ["select", "selectArea"],
  computed: {
    /**
     * Returns islands sorted according to the sortBy prop
     * @returns {Array} Sorted array of islands
     */
    sortedIslands() {
      if (this.sortBy === "alphabetical") {
        return [...this.islands].sort((a, b) => {
          const nameA = (a[this.nameField] || "").toLowerCase();
          const nameB = (b[this.nameField] || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      // 'original' - return as-is (CSV order)
      return this.islands;
    }
  },
  methods: {
    /**
     * Handles island selection and emits the select event
     * @param {Object} island - The selected island object
     */
    handleSelect(island) {
      // Add type property to match the format expected by selectItem
      const islandWithType = { ...island, type: "island" };
      this.$emit("select", islandWithType);
    },
    /**
     * Handles area/polygon selection and emits the selectArea event.
     * The parent component should use the area.layer property to activate
     * the appropriate map layer before selecting the item.
     * @param {Object} area - The selected area object with layer info
     */
    handleAreaSelect(area) {
      this.$emit("selectArea", area);
    },
    /**
     * Handles keyboard navigation for the list items
     * @param {KeyboardEvent} event - The keyboard event
     * @param {Object} item - The item being navigated
     * @param {String} section - The section type ('area' or 'island')
     */
    handleKeyDown(event, item, section) {
      // Find the parent list container
      const listContainer = event.target.closest(".island-list");
      if (!listContainer) return;

      // Get all buttons within this list
      const allButtons = Array.from(listContainer.querySelectorAll("button"));
      const currentIndex = allButtons.indexOf(event.target);

      if (currentIndex === -1) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          const nextIndex =
            currentIndex < allButtons.length - 1 ? currentIndex + 1 : 0;
          allButtons[nextIndex].focus();
          break;
        case "ArrowUp":
          event.preventDefault();
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : allButtons.length - 1;
          allButtons[prevIndex].focus();
          break;
        case "Home":
          event.preventDefault();
          allButtons[0].focus();
          break;
        case "End":
          event.preventDefault();
          allButtons[allButtons.length - 1].focus();
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (section === "area") {
            this.handleAreaSelect(item);
          } else {
            this.handleSelect(item);
          }
          break;
        default:
          // Allow other keys to work normally
          return;
      }
    }
  },
  mounted() {
    // Focus the first button when component mounts for keyboard users
    this.$nextTick(() => {
      const firstButton = this.$el.querySelector(".island-list button");
      if (firstButton) {
        // Don't auto-focus, but ensure it's focusable
        firstButton.setAttribute("tabindex", "0");
      }
    });
  }
};
</script>

<style scoped>
.island-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.island-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.875rem 1rem;
  background-color: var(--bs-white);
  border: 1px solid var(--bs-gray-200);
  border-radius: 0.5rem;
  text-align: left;
  font-size: 0.9375rem;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.island-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background-color: var(--bs-primary);
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.island-item:hover {
  background-color: #f8f9fa;
  border-color: var(--bs-primary);
  color: #2c3e50;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.island-item:hover::before {
  transform: scaleY(1);
}

.island-item:hover .island-chevron {
  color: var(--bs-primary);
  transform: translateX(2px);
}

.island-item:active {
  transform: translateX(1px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.island-item:focus {
  outline: none;
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.island-name {
  font-weight: 500;
  letter-spacing: -0.01em;
  flex: 1;
}

.island-chevron {
  font-size: 0.875rem;
  color: var(--bs-secondary);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  margin-left: 0.75rem;
}

/* Area icon styling */
.area-icon {
  color: var(--bs-secondary);
  font-size: 1rem;
}

.area-item:hover .area-icon {
  color: var(--bs-primary);
}

/* Active/selected state (if needed in future) */
.island-item.active {
  background-color: rgba(13, 110, 253, 0.08);
  border-color: var(--bs-primary);
  color: var(--bs-primary);
}

.island-item.active::before {
  transform: scaleY(1);
}

.island-item.active .island-chevron {
  color: var(--bs-primary);
}
</style>
